import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Calendar, MapPin, Clock, ArrowRight, Users } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Events() {
  usePageMeta({
    title: "Events & Community, Navica Supports",
    description: "Upcoming community events, wellbeing days and workshops run by Navica Supports across Sydney. Free and open to NDIS participants and families.",
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/events`);
      setEvents(data || []);
    } catch (e) {
      console.error("Failed to load events:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upcoming = events.filter((e) => new Date(e.event_date) >= new Date());
  const past = events.filter((e) => new Date(e.event_date) < new Date());

  return (
    <div data-testid="events-page">
      {/* HERO */}
      <section className="pt-16 lg:pt-24 pb-16 relative overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#EA823A] font-semibold mb-5 fade-up">Community Events</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-[#1F2A2E] leading-[1.05] mb-6 fade-up fade-up-1">
            Show up. <span className="italic" style={{ color: "var(--navica-coral)" }}>Belong.</span> Connect.
          </h1>
          <p className="text-xl text-[#4A595E] leading-relaxed max-w-3xl fade-up fade-up-2">
            From coffee catch-ups to NDIS info sessions and lived-experience panels. Our community events create space to meet, learn and feel less alone.
          </p>
        </div>
      </section>

      {/* UPCOMING */}
      <section className="pb-20" data-testid="upcoming-events">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-display text-3xl md:text-4xl text-[#1F2A2E]">Upcoming events</h2>
            <span className="text-sm text-[#6B7A80]">{upcoming.length} scheduled</span>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {["sk1", "sk2", "sk3"].map((k) => (
                <div key={k} className="h-80 rounded-3xl bg-[#F1E4D5] animate-pulse" />
              ))}
            </div>
          ) : upcoming.length === 0 ? (
            <div className="text-center py-20 bg-[#F1E4D5] rounded-[2.5rem] border border-[#F0E5DE]" data-testid="no-upcoming">
              <Users className="w-12 h-12 text-[#E2718A] mx-auto mb-4" />
              <h3 className="font-display text-2xl text-[#1F2A2E] mb-3">New events landing soon</h3>
              <p className="text-[#4A595E] max-w-md mx-auto mb-6">We're planning our next round of community catch-ups. Subscribe to be the first to know.</p>
              <a href={`mailto:info@navicasupports.com?subject=Notify%20me%20about%20events`} className="btn-primary">Notify me <ArrowRight className="w-4 h-4" /></a>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcoming.map((event, idx) => (
                <EventCard key={event.id} event={event} idx={idx} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* PAST */}
      {past.length > 0 && (
        <section className="pb-24 bg-[#F1E4D5]" data-testid="past-events">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
            <h2 className="font-display text-3xl md:text-4xl text-[#1F2A2E] mb-10">Past gatherings</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {past.map((event, idx) => (
                <EventCard key={event.id} event={event} idx={idx} muted />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20" data-testid="event-cta">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-display text-4xl md:text-5xl text-[#1F2A2E] leading-tight mb-6">
            Have an idea for an event?
          </h2>
          <p className="text-lg text-[#4A595E] leading-relaxed mb-8">
            We love co-creating community spaces with participants, carers and partner organisations. Get in touch with your idea.
          </p>
          <a href="/contact" className="btn-primary">Pitch an event idea <ArrowRight className="w-4 h-4" /></a>
        </div>
      </section>
    </div>
  );
}

function EventCard({ event, idx, muted }) {
  const date = new Date(event.event_date);
  const month = date.toLocaleString("en-AU", { month: "short" }).toUpperCase();
  const day = date.getDate();
  return (
    <div
      className={`card-soft fade-up flex flex-col ${muted ? "opacity-75" : ""}`}
      style={{ animationDelay: `${idx * 0.07}s` }}
      data-testid={`event-card-${event.id}`}
    >
      <div className="flex items-start gap-5 mb-5">
        <div className="rounded-2xl bg-[#FCE7EB] text-center px-4 py-3 shrink-0">
          <div className="text-[10px] tracking-wider font-bold text-[#E2718A]">{month}</div>
          <div className="font-display text-3xl text-[#1F2A2E] leading-none mt-0.5">{day}</div>
        </div>
        <div className="flex-1">
          {event.category && (
            <span className="inline-block text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#F1E4D5] text-[#EA823A] font-semibold mb-2">
              {event.category}
            </span>
          )}
          <h3 className="font-display text-xl text-[#1F2A2E] leading-snug">{event.title}</h3>
        </div>
      </div>
      <p className="text-sm text-[#4A595E] leading-relaxed mb-5 flex-1">{event.description}</p>
      <div className="space-y-2 text-sm text-[#284952] border-t border-[#F0E5DE] pt-4">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#EA823A]" />
          <span>{event.time || "TBA"}</span>
        </div>
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#EA823A]" />
          <span>{event.location}</span>
        </div>
      </div>
      {!muted && event.rsvp_link && (
        <a href={event.rsvp_link} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#E2718A]">
          RSVP <ArrowRight className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}
